import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Chip, IconoAlerta, useAvisos, Ventana } from '@tallerya/ui';
import { useRef, useState } from 'react';
import {
  useImportacionClientes,
  type FilaConError,
  type FormatoPlantilla,
  type ProblemaCliente,
} from './datos';

// «Importar clientes» del Figma (#106), en dos pasos sobre el listado:
//
//   1. Subir el archivo: la plantilla para descargar y la zona para soltarlo.
//      «Importar» aquí quiere decir «leer y revisar»; todavía no se guarda nada.
//   2. Revisar: cuántos clientes están listos, la tabla de las filas con error, e
//      «Importar los N válidos».
//
// El archivo se queda en la pantalla entre los dos pasos y se manda otra vez al
// importar: la API no guarda la revisión, y vuelve a revisar antes de dar de
// alta. Si en la segunda revisión alguna fila ya no sirve —otra persona dio de
// alta ese documento mientras tanto—, la ventana no se cierra: muestra lo que
// sí entró y la tabla de lo que no.

/** El traductor de `useTraduccion`, para las funciones de abajo que arman textos. */
type Traductor = ReturnType<typeof useTraduccion>['t'];

/** Lo que dice la pantalla; la API lo vuelve a comprobar. */
const TOPE_BYTES = 5 * 1024 * 1024;
const EXTENSIONES = ['.xlsx', '.csv'];

type Etapa =
  | { tipo: 'subir' }
  | { tipo: 'revisar'; total: number; validos: number; errores: FilaConError[] }
  | { tipo: 'hecho'; creados: number; errores: FilaConError[] };

interface Props {
  abierta: boolean;
  onCerrar: () => void;
}

export function ImportarClientes({ abierta, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const importacion = useImportacionClientes();
  const selector = useRef<HTMLInputElement>(null);
  // Hay un archivo leyéndose a memoria. Ver `elegir`.
  const leyendo = useRef(false);

  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'subir' });
  const [archivo, setArchivo] = useState<File | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);
  // La importación se cortó a mitad: «Importar» queda deshabilitado. Ver `importar`.
  const [seCorto, setSeCorto] = useState(false);

  // Todo es de ESTE archivo: al cerrar se olvida, o la próxima vez arrancaría en
  // la revisión del anterior.
  function cerrar() {
    if (ocupado) return;
    setEtapa({ tipo: 'subir' });
    setArchivo(null);
    setProblema(null);
    setSeCorto(false);
    onCerrar();
  }

  /** Lo que se puede decir sin mandar nada: la extensión y el tamaño. */
  async function elegir(elegido: File | undefined) {
    // Con una revisión o una importación en curso no se cambia el archivo: lo que
    // se importa tiene que ser lo que se revisó.
    // `leyendo` y no solo `ocupado`: el estado cambia en el próximo dibujado, y dos
    // archivos soltados en el mismo instante lo verían los dos en falso.
    if (ocupado || leyendo.current || !elegido) return;
    setProblema(null);
    const extension = elegido.name.slice(elegido.name.lastIndexOf('.')).toLowerCase();
    if (!EXTENSIONES.includes(extension)) {
      setProblema(t('error.customer_import.formato_no_soportado'));
      return;
    }
    if (elegido.size > TOPE_BYTES) {
      setProblema(t('error.customer_import.archivo_muy_grande'));
      return;
    }
    // Una copia en memoria, como en el catálogo (`ImportarItems`): el `File` del
    // selector apunta al disco, y si se corrige en Excel entre los dos pasos,
    // Chromium falla la subida y otros suben un archivo distinto del revisado.
    // Mientras se lee, la ventana está ocupada: ni Revisar ni Cancelar, para que
    // la copia no aterrice sobre una revisión en curso o una ventana ya cerrada.
    leyendo.current = true;
    setOcupado(true);
    try {
      setArchivo(new File([await elegido.arrayBuffer()], elegido.name, { type: elegido.type }));
    } catch {
      setProblema(t('error.customer_import.archivo_ilegible'));
    } finally {
      leyendo.current = false;
      setOcupado(false);
    }
  }

  async function revisar() {
    if (!archivo) return;
    await correr(async () => {
      const paso = await importacion.revisar(archivo);
      if (!paso.datos) return paso.problema;
      setEtapa({
        tipo: 'revisar',
        // Enteros del contrato: llegan como `number | string`, igual que la versión.
        total: Number(paso.datos.totalRows),
        validos: Number(paso.datos.validCount),
        errores: paso.datos.errors,
      });
    });
  }

  /**
   * No va por `correr`: un fallo acá no siempre quiere decir «no pasó nada». Si el
   * rechazo es del archivo —formato, ilegible, sin filas—, el servidor no guardó
   * nada y se puede corregir y reintentar. Cualquier otro —un 500, sin respuesta—
   * pudo cortar la importación a mitad, con lotes ya guardados: reintentar
   * duplicaría los clientes sin código ni documento, así que se avisa y se
   * deshabilita «Importar».
   */
  async function importar() {
    if (!archivo) return;
    setOcupado(true);
    setProblema(null);
    try {
      const paso = await importacion.importar(archivo);
      if (!paso.datos) {
        if (esDelArchivo(paso.problema)) setProblema(textoDelRechazo(paso.problema, t));
        else avisarCorte();
        return;
      }
      const { errors } = paso.datos;
      const created = Number(paso.datos.created);
      avisar({ titulo: t('admin.clientes.importar.aviso', { n: created }) });
      if (errors.length === 0) {
        cerrar();
        return;
      }
      setEtapa({ tipo: 'hecho', creados: created, errores: errors });
    } catch {
      avisarCorte();
    } finally {
      setOcupado(false);
    }
  }

  function avisarCorte() {
    setSeCorto(true);
    setProblema(t('admin.clientes.importar.cortada'));
  }

  async function plantilla(formato: FormatoPlantilla) {
    await correr(async () => {
      if (!(await importacion.plantilla(formato))) return {};
    });
  }

  /** Corre un paso con la ventana ocupada y dice cómo salió. */
  async function correr(paso: () => Promise<ProblemaCliente | undefined | void>) {
    setOcupado(true);
    setProblema(null);
    try {
      const rechazo = await paso();
      if (rechazo) setProblema(textoDelRechazo(rechazo, t));
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
    } finally {
      setOcupado(false);
    }
  }

  const aviso = problema && (
    <AvisoError aviso={{ titulo: t('admin.clientes.importar.error.titulo'), texto: problema }} />
  );

  if (etapa.tipo === 'hecho') {
    return (
      <Ventana
        abierta={abierta}
        ancho={640}
        titulo={t('admin.clientes.importar.hecho.titulo', { n: etapa.creados })}
        descripcion={t('admin.clientes.importar.hecho.texto', { n: etapa.errores.length })}
        acciones={<Boton onClick={cerrar}>{t('admin.clientes.importar.entendido')}</Boton>}
      >
        <TablaDeErrores filas={etapa.errores} />
      </Ventana>
    );
  }

  if (etapa.tipo === 'revisar') {
    return (
      <Ventana
        abierta={abierta}
        ancho={640}
        titulo={t('admin.clientes.importar.revisar.titulo')}
        descripcion={t('admin.clientes.importar.revisar.texto', {
          n: etapa.total,
          archivo: archivo?.name ?? '',
        })}
        acciones={
          <>
            <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
              {t('app.cancelar')}
            </Boton>
            <Boton
              disabled={ocupado || seCorto || etapa.validos === 0}
              aria-busy={ocupado}
              onClick={() => void importar()}
            >
              {t('admin.clientes.importar.revisar.boton', { n: etapa.validos })}
            </Boton>
          </>
        }
      >
        <p className="importar__resumen">
          <Chip tono="exito">{t('admin.clientes.importar.revisar.listos', { n: etapa.validos })}</Chip>
          {etapa.errores.length > 0 && (
            <Chip>{t('admin.clientes.importar.revisar.conErrores', { n: etapa.errores.length })}</Chip>
          )}
        </p>
        {aviso}
        {etapa.errores.length > 0 && <TablaDeErrores filas={etapa.errores} />}
        {/* Antes del botón y no después de importar (#178): quien importa las
            válidas y luego sube el archivo corregido entero repite los clientes
            sin código ni documento, que no hay con qué reconocer. Sin válidas no
            hay nada que importar ahora, y la advertencia no aplica. */}
        {etapa.errores.length > 0 && etapa.validos > 0 && (
          <p className="importar__advertencia">
            <IconoAlerta />
            <span>{t('admin.clientes.importar.revisar.advertencia')}</span>
          </p>
        )}
      </Ventana>
    );
  }

  return (
    <Ventana
      abierta={abierta}
      titulo={t('admin.clientes.importar.titulo')}
      info={t('admin.clientes.importar.texto')}
      acciones={
        <>
          <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={ocupado || !archivo} aria-busy={ocupado} onClick={() => void revisar()}>
            {t('admin.clientes.importar.boton')}
          </Boton>
        </>
      }
    >
      {aviso}
      {/* El mismo marcado que la importación del catálogo (`ImportarItems`), que
          es la que define los estilos `importar__*`. */}
      <p className="importar__plantilla">
        {t('admin.clientes.importar.plantilla')}{' '}
        <Boton variante="texto" disabled={ocupado} onClick={() => void plantilla('xlsx')}>
          {t('admin.clientes.importar.plantillaExcel')}
        </Boton>
        <Boton variante="texto" disabled={ocupado} onClick={() => void plantilla('csv')}>
          {t('admin.clientes.importar.plantillaCsv')}
        </Boton>
      </p>
      <div
        className={`importar__zona${arrastrando ? ' importar__zona--encima' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          void elegir(e.dataTransfer.files[0]);
        }}
      >
        {archivo ? (
          <p>
            <strong>{archivo.name}</strong>{' '}
            <Boton variante="texto" disabled={ocupado} onClick={() => selector.current?.click()}>
              {t('admin.clientes.importar.cambiar')}
            </Boton>
          </p>
        ) : (
          <p>
            {t('admin.clientes.importar.zona')}{' '}
            <Boton variante="texto" disabled={ocupado} onClick={() => selector.current?.click()}>
              {t('admin.clientes.importar.elegir')}
            </Boton>
          </p>
        )}
        <small>{t('admin.clientes.importar.formatos')}</small>
        <input
          ref={selector}
          type="file"
          accept={EXTENSIONES.join(',')}
          hidden
          onChange={(e) => {
            void elegir(e.target.files?.[0]);
            // Para que volver a elegir el mismo archivo, ya corregido, dispare otra vez.
            e.target.value = '';
          }}
        />
      </div>
    </Ventana>
  );
}

/** Fila, Nombre y Problema, como en el Figma. Una fila con dos motivos los dice juntos. */
function TablaDeErrores({ filas }: { filas: FilaConError[] }) {
  const { t } = useTraduccion();

  return (
    <div className="importar__tabla">
      <table>
        <thead>
          <tr>
            <th>{t('admin.clientes.importar.columnaFila')}</th>
            <th>{t('admin.clientes.importar.columnaNombre')}</th>
            <th>{t('admin.clientes.importar.columnaProblema')}</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const problemas = f.problems.map((p) => textoDelMotivo(p, t)).join(' · ');
            return (
              <tr key={String(f.row)}>
                <td>{f.row}</td>
                <td>{f.name ?? '—'}</td>
                <td title={problemas}>{problemas}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * «Correo: No es un correo válido (ana.hdez@)». La columna va primero porque el
 * texto del código es el genérico del formulario —«Este campo es obligatorio»—, y
 * sin ella no se sabe cuál.
 */
function textoDelMotivo(
  motivo: FilaConError['problems'][number],
  t: Traductor,
): string {
  const texto = traducirError(motivo.code, t);
  if (!motivo.column) return texto;
  const columna = etiquetaDeColumna(motivo.column, t);
  return motivo.value
    ? t('admin.clientes.importar.motivoConValor', { columna, texto, valor: motivo.value })
    : t('admin.clientes.importar.motivo', { columna, texto });
}

/** Las columnas de la plantilla, como se leen en la pantalla. */
function etiquetaDeColumna(columna: string, t: Traductor): string {
  switch (columna) {
    case 'codigo':
      return t('admin.clientes.columna.codigo');
    case 'tipo':
      return t('admin.clientes.columna.tipo');
    case 'nombre':
      return t('admin.clientes.columna.nombre');
    case 'nombre_comercial':
      return t('admin.clientes.formulario.nombreComercial');
    case 'tipo_documento':
      return t('admin.clientes.importar.columnaTipoDocumento');
    case 'numero_documento':
      return t('admin.clientes.columna.documento');
    case 'telefono':
      return t('admin.clientes.columna.telefono');
    case 'correo':
      return t('admin.clientes.columna.correo');
    case 'direccion':
      return t('admin.clientes.importar.columnaDireccion');
    case 'notas':
      return t('admin.clientes.importar.columnaNotas');
    default:
      return columna;
  }
}

/**
 * Un rechazo del archivo entero. El del validador —extensión, tamaño— llega como
 * error por campo; el de la lectura —no se pudo leer, faltan columnas—, con su
 * código suelto.
 */
/**
 * Si el rechazo es del archivo y no de la importación a medias: los códigos de la
 * revisión (`customer_import.*`) y los del validador —extensión, tamaño—, que
 * llegan como errores por campo. Con cualquiera de ellos el servidor no guardó
 * nada.
 */
function esDelArchivo(rechazo: ProblemaCliente): boolean {
  return rechazo.code === 'request.validation' || (rechazo.code?.startsWith('customer_import.') ?? false);
}

function textoDelRechazo(rechazo: ProblemaCliente, t: Traductor): string {
  const codigo = Object.values(rechazo.errors ?? {})[0]?.[0] ?? rechazo.code;
  return codigo ? traducirError(codigo, t) : t('ui.formulario.aviso.sinRespuesta');
}
